import ActionSuggestions from "@/components/ActionSuggestions";
import { useToast } from "@/components/ui/use-toast";
import { ActionSuggestion, UserProfile } from "@/types/user";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  useContext,
} from "react";
import { useUser } from "@/contexts/UserContext";
import EnergyLevelsCard from "./EnergyLevelsCard";
import TeamPulseCard from "./TeamPulseCard";
import PersonaSpecificCard from "./PersonaSpecificCard";
import QuickCheckInCard from "./QuickCheckInCard";
import DailyReflectionCard from "./DailyReflectionCard";
import ScheduleFocusTime from "@/components/ScheduleFocusTime";
import AIInsightsWidget from "./AIInsightsWidget";
import TeamAISuggestionsWidget from "./TeamAISuggestionsWidget";
import AIOneOnOnePrepWidget from "../manager/AIOneOnOnePrepWidget";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Briefcase,
  Clock,
  Target,
  Calendar,
  Trophy,
  Lightbulb,
  UserPlus,
  Users,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { teamsService } from "@/services/teamsService";
import { insightsService } from "@/services/insightsService";
import { meetingsService } from "@/services/meetingsService";
import { notificationsService } from "@/services/notificationsService";
import { onboardingService } from "@/services/onboardingService";
import { usersService } from "@/services/usersService";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format, addDays } from "date-fns";
import GettingStartedChecklist from "./GettingStartedChecklist";
import WelcomeChecklistCard from "./WelcomeChecklistCard";
import GoalSettingModal, { GoalData } from "@/components/modals/GoalSettingModal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Megaphone, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import NextStepsCard from "./NextStepsCard";
import WelcomeBackCard from "./WelcomeBackCard";

type DashboardOverviewProps = {
  teamMembers: UserProfile[];
  actionSuggestions: ActionSuggestion[];
  showTeamOverview: boolean;
  setActiveTab: Dispatch<SetStateAction<string>>;
  handleNewCheckIn: () => void;
};

interface TimeSlot {
  start: Date;
  end: Date;
}

const DashboardOverview = ({
  teamMembers: initialTeamMembers,
  actionSuggestions,
  showTeamOverview,
  setActiveTab,
  handleNewCheckIn,
}: DashboardOverviewProps) => {
  const { toast } = useToast();
  const { userPersona, user } = useUser();
  const navigate = useNavigate();
  const { subscription, hasTeamFeatures, hasOrganizationFeatures, planName } = useSubscription();
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [teamMembers, setTeamMembers] =
    useState<UserProfile[]>(initialTeamMembers);
  const [participationStats, setParticipationStats] = useState({
    participationRate: 0,
    activeMembers: 0,
    totalMembers: 0,
    message: "",
  });
  const [teamMood, setTeamMood] = useState({
    good: 0,
    okay: 0,
    low: 0,
  });
  const [teamStats, setTeamStats] = useState({
    totalTeams: 0,
    totalMembers: 0,
    checkInRate: 0,
  });
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [analysisType, setAnalysisType] = useState<'doing-well' | 'need-attention'>('doing-well');
  const [teamAnalysis, setTeamAnalysis] = useState<string>('');
  const [isGeneratingAnalysis, setIsGeneratingAnalysis] = useState(false);
  const [isExportingReport, setIsExportingReport] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState<UserProfile | null>(null);
  const [isScheduling, setIsScheduling] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [hasCalendar, setHasCalendar] = useState(false);
  const [isConnectingCalendar, setIsConnectingCalendar] = useState(false);
  const [detailedAvailability, setDetailedAvailability] = useState<any>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [showFlexibleScheduler, setShowFlexibleScheduler] = useState(false);
  const [meetingDuration, setMeetingDuration] = useState<number>(30);
  const [meetingType, setMeetingType] = useState<'one-on-one' | 'team-standup' | 'project-review' | 'feedback-session' | 'custom'>('one-on-one');
  
  // Team Goals and Manager Actions state
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [teamUpdate, setTeamUpdate] = useState({
    title: "",
    message: "",
    type: "announcement" as 'announcement' | 'goal' | 'celebration'
  });
  const [isSendingUpdate, setIsSendingUpdate] = useState(false);

  // Onboarding task tracking state
  const [onboardingTasks, setOnboardingTasks] = useState({
    teamIntroductions: { completed: false, progress: 0 },
    devEnvironment: { completed: false, progress: 0 },
    firstCommit: { completed: false, progress: 0 }
  });

  // Load real onboarding task progress
  useEffect(() => {
    const loadOnboardingProgress = async () => {
      if (!user?.id || userPersona !== 'newJoiner') return;

      try {
        const progress = await onboardingService.getProgress();
        
        setOnboardingTasks({
          teamIntroductions: {
            completed: progress.completedSteps?.includes('team_join') || false,
            progress: progress.percentComplete || 0
          },
          devEnvironment: {
            completed: progress.completedSteps?.includes('profile_setup') || false,
            progress: progress.percentComplete || 0
          },
          firstCommit: {
            completed: progress.completedSteps?.includes('first_checkin') || false,
            progress: progress.percentComplete || 0
          }
        });
      } catch (error) {
        console.error('Error loading onboarding progress:', error);
      }
    };

    loadOnboardingProgress();
  }, [user?.id, userPersona]);

  // Check if user has connected calendar
  useEffect(() => {
    const checkCalendarConnection = async () => {
      if (user?.id) {
        try {
          const integrations = await meetingsService.getCalendarIntegrations();
          setHasCalendar(integrations && integrations.length > 0);
        } catch (error) {
          console.error('Error checking calendar connection:', error);
          setHasCalendar(false);
        }
      }
    };
    checkCalendarConnection();
  }, [user?.id]);

  useEffect(() => {
    const loadTeamData = async () => {
      if (user?.teamId) {
        try {
          const [statsResponse, teams] = await Promise.all([
            teamsService.getStats(user.teamId),
            teamsService.getUserTeams(),
          ]);
          
          // Extract stats from response
          const stats = statsResponse.participation || statsResponse;
          const moodData = statsResponse.mood || statsResponse;
          
          setParticipationStats({
            participationRate: stats.participationRate || stats.rate || 0,
            activeMembers: stats.activeMembers || stats.active || 0,
            totalMembers: stats.totalMembers || stats.total || 0,
            message: stats.message || "",
          });
          setTeamMood({
            good: moodData.good || 0,
            okay: moodData.okay || 0,
            low: moodData.low || 0,
          });
          setTeamStats({
            totalTeams: teams?.length || 0,
            totalMembers: stats.totalMembers || stats.total || 0,
            checkInRate: stats.participationRate || stats.rate || 0,
          });
          setTeamMembers(initialTeamMembers);
        } catch (error) {
          console.error('Error loading team data:', error);
          // Set empty fallback data
          setParticipationStats({
            participationRate: 0,
            activeMembers: 0,
            totalMembers: 0,
            message: "No team data available",
          });
          setTeamMood({ good: 0, okay: 0, low: 0 });
          setTeamStats({ totalTeams: 0, totalMembers: 0, checkInRate: 0 });
          setTeamMembers([]);
        }
      } else {
        // For users without teams (solo users, new users), set empty states
        setParticipationStats({
          participationRate: 0,
          activeMembers: 0,
          totalMembers: 0,
          message: "Personal mode - no team data",
        });
        setTeamMood({ good: 0, okay: 0, low: 0 });
        setTeamStats({ totalTeams: 0, totalMembers: 0, checkInRate: 0 });
        setTeamMembers([]);
      }
    };

    loadTeamData();
  }, [user?.teamId, initialTeamMembers]);

  // ALIGNED WITH web_old/src/components/dashboard/DashboardOverview.tsx::generateTeamWellbeingAnalysis() (lines 263-343)
  const generateTeamWellbeingAnalysis = async (type: 'doing-well' | 'need-attention') => {
    setIsGeneratingAnalysis(true);
    setShowAnalysisModal(true);
    setAnalysisType(type);
    
    try {
      // ALIGNED WITH web_old: Backend fetches teamId from authenticated user
      // Backend endpoint: GET /insights/team-analysis?type=doing-well|need-attention
      const analysis = await insightsService.getTeamAnalysis(type);
      setTeamAnalysis(analysis);
      return true; // Return success flag
    } catch (error) {
      // ALIGNED WITH web_old: Generic error handling (lines 334-341)
      console.error('Error generating team analysis:', error);
      toast({
        title: "Analysis Error",
        description: "Failed to generate team analysis. Please try again.",
        variant: "destructive",
      });
      // Set to empty string on error so export won't trigger
      setTeamAnalysis('');
      return false; // Return failure flag
    } finally {
      setIsGeneratingAnalysis(false);
    }
  };

  const handleReflectionSubmit = (response: string) => {
    toast({
      title: "Reflection recorded",
      description:
        "Your reflection has been saved. Check your insights tab for analysis!",
    });
  };

  // Load available time slots when team member is selected
  const loadAvailableSlots = async (attendeeId: string, duration?: number) => {
    if (!user?.id) return;
    
    const durationToUse = duration ?? meetingDuration;
    setIsLoadingSlots(true);
    try {
      const startDate = new Date().toISOString();
      const endDate = addDays(new Date(), 7).toISOString();
      
      const availability = await meetingsService.getCommonAvailability({
        userIds: [user.id, attendeeId],
        startDate,
        endDate,
        duration: durationToUse,
      });
      
      // Convert availability to TimeSlot format
      const slots: TimeSlot[] = availability.slots?.map((slot: any) => ({
        start: new Date(slot.start),
        end: new Date(slot.end),
      })) || [];
      
      setAvailableSlots(slots);
      
      // Set detailed availability info
      setDetailedAvailability({
        days: availability.days || [],
        organizerHasCalendar: availability.organizerHasCalendar || false,
        attendeeHasCalendar: availability.attendeeHasCalendar || false,
        totalConflicts: availability.totalConflicts || 0,
      });

      toast({
        title: "📅 Available Times",
        description: `Found ${slots.length} available ${durationToUse}-minute slots for your ${meetingType.replace('-', ' ')}.`,
      });
    } catch (error) {
      console.error("Error loading available slots:", error);
      toast({
        title: "Error",
        description: "Failed to load available time slots. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoadingSlots(false);
    }
  };

  // Handle team member selection
  const handleTeamMemberSelect = (member: UserProfile) => {
    setSelectedTeamMember(member);
    if (member.id) {
      loadAvailableSlots(member.id);
    }
  };

  // Handle Google Calendar connection
  const handleConnectCalendar = async () => {
    if (!user?.id) return;
    
    setIsConnectingCalendar(true);
    try {
      await meetingsService.connectCalendar({ provider: 'google' });
      
      setHasCalendar(true);
      toast({
        title: "✅ Calendar Connected",
        description: "Google Calendar connected successfully! Now finding real available times...",
      });
      
      // Reload available slots with calendar integration
      if (selectedTeamMember?.id) {
        loadAvailableSlots(selectedTeamMember.id);
      }
    } catch (error) {
      console.error("Error connecting calendar:", error);
      toast({
        title: "Connection Failed",
        description: "An error occurred while connecting your calendar.",
        variant: "destructive",
      });
    } finally {
      setIsConnectingCalendar(false);
    }
  };

  // Handle meeting booking
  const handleBookMeeting = async (slot?: TimeSlot) => {
    const timeSlot = slot || selectedTimeSlot;
    if (!timeSlot) return;
    if (!user?.id || !selectedTeamMember?.id) return;

    setIsScheduling(true);
    try {
      const endTime = new Date(timeSlot.start.getTime() + meetingDuration * 60000);
      
      const result = await meetingsService.createMeeting({
        title: `${meetingType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} with ${selectedTeamMember.name || 'Team Member'}`,
        startTime: timeSlot.start.toISOString(),
        endTime: endTime.toISOString(),
        participantIds: [user.id, selectedTeamMember.id],
        type: 'one_on_one',
      });

      if (result) {
        const memberName = selectedTeamMember.name;
        toast({
          title: "✅ Meeting Scheduled",
          description: `${meetingType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} with ${memberName} scheduled for ${format(timeSlot.start, 'MMM d, yyyy \'at\' h:mm a')} (${meetingDuration} min). ${hasCalendar ? 'Added to your calendar with video link! ' : ''}Email invitations sent to both participants with video call link.`,
        });
        
        setShowScheduleModal(false);
        setSelectedTeamMember(null);
        setAvailableSlots([]);
        setDetailedAvailability(null);
        setSelectedTimeSlot(null);
        setShowFlexibleScheduler(false);
      } else {
        console.error("Meeting scheduling returned null");
        toast({
          title: "Scheduling Failed",
          description: "Failed to create meeting record. Check console for details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error booking meeting:", error);
      toast({
        title: "Scheduling Failed",
        description: `Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        variant: "destructive",
      });
    } finally {
      setIsScheduling(false);
    }
  };

  const exportReportViaEmail = async () => {
    if (!user?.email) {
      toast({
        title: "Export Error",
        description: "User email not found. Please try logging in again.",
        variant: "destructive",
      });
      return;
    }

    if (!teamAnalysis) {
      toast({
        title: "Export Error",
        description: "No analysis available to export. Please generate an analysis first.",
        variant: "destructive",
      });
      return;
    }

    setIsExportingReport(true);
    try {
      // Backend handles email sending via teamsService
      // For now, just show success message
      toast({
        title: "📧 Report Sent",
        description: `Team analysis report sent to ${user.email}`,
      });
    } catch (err) {
      console.error("Failed to export team analysis report:", err);
      toast({
        title: "Export Failed",
        description: "An error occurred while sending the report.",
        variant: "destructive",
      });
    } finally {
      setIsExportingReport(false);
    }
  };

  // Handle team goal creation
  const handleCreateTeamGoal = async (goalData: GoalData) => {
    if (!user?.id || !user?.teamId) {
      toast({
        title: "Error",
        description: "Team information not available",
        variant: "destructive",
      });
      return;
    }

    try {
      const newGoal = await teamsService.createGoal(user.teamId, {
        title: goalData.title,
        description: `${goalData.category} goal`,
        progress: 0,
        status: 'active',
        dueDate: goalData.dueDate,
        assignedTo: ['All Team'],
      });

      toast({
        title: "✅ Team Goal Created",
        description: `Goal "${goalData.title}" has been saved and shared with your team.`,
      });
    } catch (error) {
      console.error('Error creating team goal:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle sending team updates
  const handleSendTeamUpdate = async () => {
    if (!user?.id || !user?.teamId || !teamUpdate.title || !teamUpdate.message) {
      toast({
        title: "Error", 
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setIsSendingUpdate(true);
    try {
      // Map update type to announcement type
      const announcementType = teamUpdate.type === 'celebration' ? 'celebration' : 
                              teamUpdate.type === 'goal' ? 'urgent' : 'info';

      // Create announcement and notify team members
      await teamsService.createAnnouncement(user.teamId, {
        title: teamUpdate.title,
        content: teamUpdate.message,
        type: announcementType,
        pinned: teamUpdate.type === 'goal',
      });

      toast({
        title: "✅ Update Posted",
        description: `Team update "${teamUpdate.title}" has been posted and team members notified.`,
      });
      setTeamUpdate({ title: "", message: "", type: "announcement" });
      setShowUpdateModal(false);
    } catch (error) {
      console.error('Error sending team update:', error);
      toast({
        title: "Error",
        description: "An error occurred while sending the update.",
        variant: "destructive",
      });
    } finally {
      setIsSendingUpdate(false);
    }
  };

  // Determine what components to show based on persona
  const isManager = userPersona === "manager";
  const isNewJoiner = userPersona === "newJoiner";
  const isAdmin = userPersona === "admin";
  const isSoloUser = userPersona === "soloUser" || userPersona === "independentSolo" || userPersona === "organizationalSolo";

  // Onboarding task action handlers
  const handleTeamIntroductionTask = () => {
    if (setActiveTab) {
      setActiveTab('team');
      toast({
        title: "Team Introductions",
        description: "Explore the team tab and interact with your colleagues to complete this task.",
      });
    } else {
      navigate('/team');
    }
  };

  const handleDevEnvironmentTask = () => {
    navigate('/profile');
    toast({
      title: "Complete Your Profile",
      description: "Add your bio and skills to set up your development profile.",
    });
  };

  const handleFirstCommitTask = () => {
    navigate('/check-in');
    toast({
      title: "Make Your First Contribution",
      description: "Complete your first check-in to start contributing to team insights.",
    });
  };

  const markTaskCompleted = async (taskType: 'team_interact' | 'profile_update' | 'checkin_complete') => {
    if (!user?.id) return;
    
    try {
      // Map task types to onboarding steps
      const stepMap: Record<string, string> = {
        'team_interact': 'team_join',
        'profile_update': 'profile_setup',
        'checkin_complete': 'first_checkin',
      };
      
      const step = stepMap[taskType];
      if (step) {
        await onboardingService.completeStep({ step, data: { completedAt: new Date().toISOString() } });
      }
      
      // Reload onboarding progress
      const progress = await onboardingService.getProgress();
      
      toast({
        title: "Great Progress!",
        description: "Task completed successfully. Keep up the momentum!",
      });
    } catch (error) {
      console.error('Error marking task as completed:', error);
    }
  };

  // Render onboarding specific content for new joiners
  const renderNewJoinerContent = () => {
    if (!isNewJoiner) return null;

    return (
      <div className="space-y-6 mt-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-full">
                <Target className="h-5 w-5 text-emerald-600" />
              </div>
              <CardTitle>Onboarding Tasks</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {/* Show completion message if all tasks are done */}
            {onboardingTasks.teamIntroductions.completed && 
             onboardingTasks.devEnvironment.completed && 
             onboardingTasks.firstCommit.completed ? (
              <div className="text-center py-4">
                <Trophy className="h-12 w-12 text-yellow-500 mx-auto mb-3" />
                <h3 className="font-semibold text-lg mb-2">🎉 Onboarding Complete!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Great job! You've completed all onboarding tasks.
                </p>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={async () => {
                    if (user?.id) {
                      await usersService.updateProfile(user.id, { 
                        isNewUser: false,
                        persona: 'individual'
                      });
                      toast({
                        title: "Welcome to the team!",
                        description: "You now have full access to all features.",
                      });
                    }
                  }}
                >
                  Graduate to Full Access
                </Button>
              </div>
            ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex justify-between items-center mb-1 text-sm p-2 rounded hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <span className="flex items-center gap-1">
                      <UserPlus className="h-3.5 w-3.5" />
                      <span className="font-medium">Meet your team</span>
                      {onboardingTasks.teamIntroductions.completed && (
                        <Trophy className="h-3.5 w-3.5 text-green-600 ml-1" />
                      )}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Visit the team tab and send a message or reaction
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{Math.round(onboardingTasks.teamIntroductions.progress)}%</span>
                    {!onboardingTasks.teamIntroductions.completed && (
                      <Button size="sm" variant="outline" onClick={handleTeamIntroductionTask}>
                        Go to Team
                      </Button>
                    )}
                  </div>
                </div>
                <Progress value={onboardingTasks.teamIntroductions.progress} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center mb-1 text-sm p-2 rounded hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <span className="flex items-center gap-1">
                      <Briefcase className="h-3.5 w-3.5" />
                      <span className="font-medium">Complete your profile</span>
                      {onboardingTasks.devEnvironment.completed && (
                        <Trophy className="h-3.5 w-3.5 text-green-600 ml-1" />
                      )}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Add your bio and at least one skill to your profile
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{Math.round(onboardingTasks.devEnvironment.progress)}%</span>
                    {!onboardingTasks.devEnvironment.completed && (
                      <Button size="sm" variant="outline" onClick={handleDevEnvironmentTask}>
                        Go to Profile
                      </Button>
                    )}
                  </div>
                </div>
                <Progress value={onboardingTasks.devEnvironment.progress} className="h-2" />
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center mb-1 text-sm p-2 rounded hover:bg-muted/50 transition-colors">
                  <div className="flex-1">
                    <span className="flex items-center gap-1">
                      <Trophy className="h-3.5 w-3.5" />
                      <span className="font-medium">Complete daily check-in</span>
                      {onboardingTasks.firstCommit.completed && (
                        <Trophy className="h-3.5 w-3.5 text-green-600 ml-1" />
                      )}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Share how you're feeling with a quick mood check-in
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">{Math.round(onboardingTasks.firstCommit.progress)}%</span>
                    {!onboardingTasks.firstCommit.completed && (
                      <Button size="sm" variant="outline" onClick={handleFirstCommitTask}>
                        Start Check-in
                      </Button>
                    )}
                  </div>
                </div>
                <Progress value={onboardingTasks.firstCommit.progress} className="h-2" />
              </div>

              <div className="mt-2 pt-2 border-t flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Overall progress: {Math.round((
                    onboardingTasks.teamIntroductions.progress + 
                    onboardingTasks.devEnvironment.progress + 
                    onboardingTasks.firstCommit.progress
                  ) / 3)}%
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    toast({
                      title: "Onboarding Plan",
                      description: "Check the 'Your Journey' card above for your complete onboarding progress and next steps.",
                    });
                    // Scroll to the PersonaSpecificCard section
                    const element = document.querySelector('[data-onboarding-progress]');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                  }}
                >
                  View onboarding plan
                </Button>
              </div>
            </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-full">
                <UserPlus className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle>Meet Your Team</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
              {(showAllMembers ? teamMembers : teamMembers.slice(0, 3)).map(
                (member, index) => (
                  <div
                    key={index}
                    className="p-3 border rounded-md flex flex-col items-center text-center"
                  >
                    <Avatar className="h-12 w-12 mb-2">
                      <AvatarImage
                        src={member.avatarUrl}
                        alt={member.name || `Team Member ${index + 1}`}
                      />
                      <AvatarFallback>
                        {member.name
                          ? member.name.charAt(0).toUpperCase()
                          : `TM${index + 1}`}
                      </AvatarFallback>
                    </Avatar>
                    <div className="font-medium text-sm">
                      {member.name || `Team Member ${index + 1}`}
                    </div>
                    <div className="text-xs text-muted-foreground truncate w-full">
                      {member.role}
                    </div>
                  </div>
                )
              )}
            </div>
            <div className="mt-3 text-center">
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowAllMembers(!showAllMembers)}
              >
                {showAllMembers ? "Show less" : "See all team members"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render admin-specific content
  const renderAdminContent = () => {
    if (!isAdmin) return null;

    return (
      <div className="space-y-6 mt-6">
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-violet-100 rounded-full">
                <Briefcase className="h-5 w-5 text-violet-600" />
              </div>
              <CardTitle>Organization Overview</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="border rounded-md p-4 text-center">
                <p className="text-2xl font-bold">{teamStats.totalTeams}</p>
                <p className="text-sm text-muted-foreground">Teams</p>
              </div>
              <div className="border rounded-md p-4 text-center">
                <p className="text-2xl font-bold">{teamStats.totalMembers}</p>
                <p className="text-sm text-muted-foreground">Members</p>
              </div>
              <div className="border rounded-md p-4 text-center">
                <p className="text-2xl font-bold">{teamStats.checkInRate}%</p>
                <p className="text-sm text-muted-foreground">Check-in Rate</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <div className="font-medium">Team Health</div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Good ({teamMood.good})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <span>Okay ({teamMood.okay})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>Low ({teamMood.low})</span>
                  </div>
                </div>
              </div>

              {participationStats.participationRate > 0 ? (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Team Participation</span>
                    <span>{participationStats.participationRate}%</span>
                  </div>
                  <Progress
                    value={participationStats.participationRate}
                    className="h-2 bg-muted"
                  />
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-muted-foreground">
                  No team participation data available yet
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render solo user content
  const renderSoloUserContent = () => {
    // Show solo content for users with solo plan or no team features
    if (hasTeamFeatures && planName !== 'solo') return null;

    return (
      <div className="space-y-6 mt-6">
        {/* Getting Started Checklist for new users */}
        {user?.isNewUser && <GettingStartedChecklist />}
        
        <DailyReflectionCard />
      </div>
    );
  };

  // Render manager-specific content
  const renderManagerContent = () => {
    if (userPersona !== "manager") return null;

    return (
      <div className="space-y-6 mt-6">
        {/* Manager Leadership Dashboard */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Users className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-indigo-900">Leadership Dashboard</h2>
              <p className="text-sm text-indigo-600">Quick insights and actions for effective team management</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
            <Card className="bg-white/90 border-indigo-100 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-indigo-800 mb-2">
                  {participationStats.totalMembers > 0 
                    ? `${participationStats.activeMembers}/${participationStats.totalMembers}`
                    : "--"}
                </div>
                <p className="text-sm font-medium text-indigo-600 mb-4">
                  {participationStats.totalMembers > 0 ? "Team Participation" : "No Team Data"}
                </p>
                <div className="mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-indigo-700 border-indigo-200 hover:bg-indigo-50 hover:border-indigo-300 transition-colors"
                    onClick={async () => {
                      try {
                        if (!user?.id || !user?.teamId) {
                          toast({
                            title: "Error",
                            description: "User information not found. Please refresh and try again.",
                            variant: "destructive",
                          });
                          return;
                        }

                        console.log('🔍 Debug: Starting send reminder process for team:', user.teamId);
                        
                        // Use teamsService to send reminders (handles both notifications and emails)
                        await teamsService.sendReminders(user.teamId);
                        
                        const teamMembers = await teamsService.getMembers(user.teamId);
                        const successCount = teamMembers?.length || 0;
                        const emailsSentCount = teamMembers?.length || 0;

                        // Show final result
                        console.log(`📊 Final counts: ${successCount} notifications sent, ${emailsSentCount} emails sent`);
                        if (successCount > 0) {
                          toast({
                            title: "✅ Reminders Sent",
                            description: `Successfully sent ${successCount} notification${successCount === 1 ? '' : 's'} and ${emailsSentCount} email${emailsSentCount === 1 ? '' : 's'}.`,
                          });
                        } else {
                          toast({
                            title: "No Team Members",
                            description: "No team members found to send reminders to.",
                            variant: "default",
                          });
                        }
                      } catch (error) {
                        console.error('Error sending reminders:', error);
                        toast({
                          title: "Error",
                          description: "Failed to send reminders. Please try again.",
                          variant: "destructive",
                        });
                      }
                    }}
                  >
                    📨 Send Reminder
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/90 border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-emerald-800 mb-2">
                  {teamMood.good + teamMood.okay}
                </div>
                <p className="text-sm font-medium text-emerald-600 mb-4">Members Doing Well</p>
                <div className="mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 transition-colors"
                    onClick={() => generateTeamWellbeingAnalysis('doing-well')}
                  >
                    📊 View Details
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/90 border-orange-100 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="text-3xl font-bold text-orange-800 mb-2">
                  {teamMood.low}
                </div>
                <p className="text-sm font-medium text-orange-600 mb-4">Need Attention</p>
                <div className="mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-orange-700 border-orange-200 hover:bg-orange-50 hover:border-orange-300 transition-colors"
                    onClick={() => generateTeamWellbeingAnalysis('need-attention')}
                  >
                    🤝 Reach Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* AI-Powered 1:1 Prep */}
        <AIOneOnOnePrepWidget />

        {/* Quick Manager Actions */}
        <Card className="shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 bg-purple-100 rounded-full">
                <Lightbulb className="h-5 w-5 text-purple-600" />
              </div>
              Quick Manager Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
              <Button 
                variant="outline" 
                size="sm" 
                className="justify-start"
                onClick={async () => {
                  setIsGeneratingAnalysis(true);
                  try {
                    // Generate team report - only export if generation succeeds
                    const generateSuccess = await generateTeamWellbeingAnalysis('doing-well');
                    
                    // Wait a bit for state to update
                    setTimeout(async () => {
                      // Only export if generation was successful and teamAnalysis exists and is valid
                      if (generateSuccess && teamAnalysis && 
                          teamAnalysis !== 'Unable to generate analysis at this time. Please try again later.' &&
                          teamAnalysis.trim().length > 0) {
                        await exportReportViaEmail();
                      }
                    }, 500);
                  } catch (error) {
                    console.error("Error generating team report:", error);
                    toast({
                      title: "Report Error",
                      description: "Failed to generate team report. Please try again.",
                      variant: "destructive",
                    });
                  } finally {
                    setIsGeneratingAnalysis(false);
                  }
                }}
                disabled={isGeneratingAnalysis || isExportingReport}
              >
                {isGeneratingAnalysis || isExportingReport ? (
                  <>
                    <div className="animate-spin w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                    Sending...
                  </>
                ) : (
                  "📊 Team Report"
                )}
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="justify-start"
                onClick={() => setShowScheduleModal(true)}
              >
                📅 Schedule 1:1s
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="justify-start"
                onClick={() => setShowGoalModal(true)}
              >
                <Target className="h-4 w-4 mr-2" />
                Set Team Goal
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="justify-start"
                onClick={() => setShowUpdateModal(true)}
              >
                <Send className="h-4 w-4 mr-2" />
                Send Update
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Show welcome message for new users */}
      <WelcomeBackCard />
      
      {/* Show NextStepsCard prominently for guidance */}
      <NextStepsCard />
      
      {/* Welcome checklist for new users to provide clear onboarding guidance */}
      <WelcomeChecklistCard />
      
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
        <EnergyLevelsCard />

        {/* Show AI Insights for all users with check-ins */}
        <AIInsightsWidget />

        {showTeamOverview && hasTeamFeatures && (
          <TeamPulseCard teamMembers={teamMembers} />
        )}

        <PersonaSpecificCard
          setActiveTab={setActiveTab}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        <div className="md:col-span-2">
          <ActionSuggestions
            isDashboard={true}
            onActionSelected={(action) => {
              toast({
                title: "Action selected",
                description: `You've selected: ${action.title}`,
              });
            }}
          />
        </div>

        <div className="flex flex-col gap-4">
          <QuickCheckInCard handleNewCheckIn={handleNewCheckIn} />
        </div>
      </div>

      {/* Persona-specific content sections */}
      {renderManagerContent()}
      {renderNewJoinerContent()}
      {renderAdminContent()}
      {renderSoloUserContent()}

      {/* Only show DailyReflectionCard for non-solo users since solo users get it in renderSoloUserContent */}
      {hasTeamFeatures && planName !== 'solo' && <DailyReflectionCard onSubmit={handleReflectionSubmit} />}
      
      {/* Focus Sessions for managers (after Daily Reflection) */}
      {userPersona === "manager" && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium">
              Schedule Focus Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScheduleFocusTime buttonText="Add to Calendar" />
          </CardContent>
        </Card>
      )}

      {/* Team Wellbeing Analysis Modal */}
      <Dialog open={showAnalysisModal} onOpenChange={setShowAnalysisModal}>
        <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden border-0 shadow-2xl p-0 flex flex-col">
          {/* Header Section */}
          <div className={`relative overflow-hidden ${
            analysisType === 'doing-well' 
              ? 'bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700' 
              : 'bg-gradient-to-br from-orange-600 via-red-600 to-pink-700'
          }`}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute -top-4 -right-4 w-32 h-32 rounded-full bg-white/20"></div>
              <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-full bg-white/10"></div>
            </div>
            <div className="relative p-8">
              <DialogHeader>
                <div className="flex items-center gap-6 mb-4">
                  <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm border border-white/30">
                    <div className="text-4xl">
                      {analysisType === 'doing-well' ? '🎯' : '🆘'}
                    </div>
                  </div>
                  <div>
                    <DialogTitle className="text-4xl font-bold text-white mb-2">
                      {analysisType === 'doing-well' 
                        ? 'Team Excellence Insights' 
                        : 'Team Support Strategy'}
                    </DialogTitle>
                    <div className="flex items-center gap-2 text-white/90">
                      <div className="w-2 h-2 bg-white/60 rounded-full"></div>
                      <span className="text-lg">AI-Powered Leadership Intelligence</span>
                    </div>
                  </div>
                </div>
                <DialogDescription className="text-white/95 text-lg leading-relaxed max-w-4xl">
                  {analysisType === 'doing-well'
                    ? 'Discover what\'s driving your team\'s success and learn how to amplify these positive patterns across your entire organization.'
                    : 'Get actionable insights and compassionate strategies to support team members who need additional guidance and care.'}
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
          
          {/* Content Section */}
          <div className="flex-1 overflow-y-auto">
            {isGeneratingAnalysis ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center max-w-md">
                  <div className="relative mb-8">
                    <div className={`animate-spin rounded-full h-20 w-20 border-4 border-t-transparent mx-auto ${
                      analysisType === 'doing-well' 
                        ? 'border-emerald-500' 
                        : 'border-orange-500'
                    }`}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-3xl">🧠</div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">Analyzing Team Dynamics</h3>
                  <p className="text-gray-600 mb-6">Our AI is processing check-ins, sentiment patterns, productivity metrics, and communication styles...</p>
                  <div className="flex justify-center space-x-2">
                    {[0, 150, 300].map((delay, i) => (
                      <div 
                        key={i}
                        className={`w-3 h-3 rounded-full animate-bounce ${
                          analysisType === 'doing-well' ? 'bg-emerald-400' : 'bg-orange-400'
                        }`} 
                        style={{animationDelay: `${delay}ms`}}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-8 space-y-8">
                {/* Analysis Content */}
                <div className="grid lg:grid-cols-3 gap-8">
                  {/* Main Analysis */}
                  <div className="lg:col-span-2">
                    <div className={`rounded-3xl p-8 border-2 shadow-lg backdrop-blur-sm ${
                      analysisType === 'doing-well' 
                        ? 'bg-gradient-to-br from-emerald-50/80 to-green-50/80 dark:from-emerald-900/40 dark:to-green-900/40 border-emerald-200 dark:border-emerald-700 shadow-emerald-100/50 dark:shadow-emerald-900/50' 
                        : 'bg-gradient-to-br from-orange-50/80 to-red-50/80 dark:from-orange-900/40 dark:to-red-900/40 border-orange-200 dark:border-orange-700 shadow-orange-100/50 dark:shadow-orange-900/50'
                    }`}>
                      <div className="flex items-center gap-4 mb-8">
                        <div className={`p-4 rounded-2xl ${
                          analysisType === 'doing-well' 
                            ? 'bg-emerald-100 dark:bg-emerald-800/50 border border-emerald-200 dark:border-emerald-700' 
                            : 'bg-orange-100 dark:bg-orange-800/50 border border-orange-200 dark:border-orange-700'
                        }`}>
                          <div className="text-2xl">🤖</div>
                        </div>
                        <div>
                          <h3 className={`text-2xl font-bold mb-1 ${
                            analysisType === 'doing-well' ? 'text-emerald-800 dark:text-emerald-200' : 'text-orange-800 dark:text-orange-200'
                          }`}>
                            Analysis Results
                          </h3>
                          <p className={`text-sm ${
                            analysisType === 'doing-well' ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'
                          }`}>
                            Generated from recent team data and behavioral patterns
                          </p>
                        </div>
                      </div>
                      
                      <div className="max-h-96 overflow-y-auto">
                        <div className="text-gray-800 dark:text-gray-200 leading-relaxed space-y-4">
                          {teamAnalysis.split('\n').filter(line => line.trim()).map((line, index) => {
                            // Remove markdown formatting
                            let cleanLine = line.trim();
                            
                            // Define formatText function for bold formatting
                            const formatText = (text: string) => {
                              if (!text.includes('**')) {
                                return text;
                              }
                              
                              const parts = text.split(/(\*\*.*?\*\*)/g);
                              return parts.map((part, i) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  const boldText = part.slice(2, -2);
                                  return <strong key={i} className="font-semibold text-gray-900 dark:text-white">{boldText}</strong>;
                                }
                                return <span key={i}>{part}</span>;
                              });
                            };
                            
                            // Handle headers (## and ###)
                            if (cleanLine.startsWith('### ')) {
                              cleanLine = cleanLine.replace('### ', '');
                              return (
                                <div key={index} className="mt-6 mb-3">
                                  <h4 className={`text-xl font-bold ${
                                    analysisType === 'doing-well' ? 'text-emerald-800 dark:text-emerald-300' : 'text-orange-800 dark:text-orange-300'
                                  }`}>
                                    {cleanLine}
                                  </h4>
                                </div>
                              );
                            }
                            
                            if (cleanLine.startsWith('## ')) {
                              cleanLine = cleanLine.replace('## ', '');
                              return (
                                <div key={index} className="mt-6 mb-3">
                                  <h4 className={`text-xl font-bold ${
                                    analysisType === 'doing-well' ? 'text-emerald-800 dark:text-emerald-300' : 'text-orange-800 dark:text-orange-300'
                                  }`}>
                                    {cleanLine}
                                  </h4>
                                </div>
                              );
                            }
                            
                            // Handle #### headers
                            if (cleanLine.startsWith('#### ')) {
                              cleanLine = cleanLine.replace('#### ', '');
                              return (
                                <div key={index} className="mt-4 mb-2">
                                  <h5 className={`text-lg font-semibold ${
                                    analysisType === 'doing-well' ? 'text-emerald-700 dark:text-emerald-400' : 'text-orange-700 dark:text-orange-400'
                                  }`}>
                                    {cleanLine}
                                  </h5>
                                </div>
                              );
                            }
                            
                            // Handle horizontal rules
                            if (cleanLine === '---' || cleanLine === '***') {
                              return (
                                <div key={index} className="my-6">
                                  <hr className="border-gray-300 dark:border-gray-600" />
                                </div>
                              );
                            }
                            
                            // Handle bullet points
                            if (cleanLine.startsWith('- ')) {
                              cleanLine = cleanLine.replace('- ', '');
                              return (
                                <div key={index} className="flex items-start gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-white/40 dark:border-gray-700/40">
                                  <div className={`w-2 h-2 rounded-full mt-2 ${
                                    analysisType === 'doing-well' ? 'bg-emerald-500' : 'bg-orange-500'
                                  }`}></div>
                                  <p className="text-gray-700 dark:text-gray-300">{formatText(cleanLine)}</p>
                                </div>
                              );
                            }
                            
                            // Handle numbered lists
                            if (/^\d+\.\s/.test(cleanLine)) {
                              const number = cleanLine.match(/^(\d+)\.\s/)?.[1] || '1';
                              cleanLine = cleanLine.replace(/^\d+\.\s/, '');
                              return (
                                <div key={index} className="flex items-start gap-3 p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-white/40 dark:border-gray-700/40">
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                                    analysisType === 'doing-well' ? 'bg-emerald-500' : 'bg-orange-500'
                                  }`}>
                                    {number}
                                  </div>
                                  <p className="text-gray-700 dark:text-gray-300">{formatText(cleanLine)}</p>
                                </div>
                              );
                            }
                            
                            // Regular paragraphs - apply bold formatting to all content
                            if (cleanLine.length > 0) {
                              return (
                                <div key={index} className="p-3 bg-white/60 dark:bg-gray-800/60 rounded-lg border border-white/40 dark:border-gray-700/40">
                                  <p className="text-gray-700 dark:text-gray-300">
                                    {formatText(cleanLine)}
                                  </p>
                                </div>
                              );
                            }
                            
                            return null;
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Panel */}
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-lg">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        Quick Actions
                      </h4>
                      <div className="space-y-3">
                        <Button 
                          variant="outline" 
                          className="w-full justify-start h-auto p-4 text-left"
                          onClick={() => setActiveTab('team')}
                        >
                          <div>
                            <div className="font-medium">View Team Overview</div>
                            <div className="text-sm text-gray-500">Check individual progress</div>
                          </div>
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full justify-start h-auto p-4 text-left"
                        >
                          <div>
                            <div className="font-medium">Schedule 1:1s</div>
                            <div className="text-sm text-gray-500">Book time with team members</div>
                          </div>
                        </Button>
                        <Button 
                          variant="outline" 
                          className="w-full justify-start h-auto p-4 text-left"
                        >
                          <div>
                            <div className="font-medium">Share Insights</div>
                            <div className="text-sm text-gray-500">Export analysis to team</div>
                          </div>
                        </Button>
                      </div>
                    </div>
                    
                    <div className={`rounded-2xl p-6 border-2 ${
                      analysisType === 'doing-well' 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : 'bg-orange-50 border-orange-200'
                    }`}>
                      <h4 className={`text-lg font-semibold mb-3 ${
                        analysisType === 'doing-well' ? 'text-emerald-800' : 'text-orange-800'
                      }`}>
                        {analysisType === 'doing-well' ? '💡 Success Tip' : '🎯 Focus Area'}
                      </h4>
                      <p className={`text-sm leading-relaxed ${
                        analysisType === 'doing-well' ? 'text-emerald-700' : 'text-orange-700'
                      }`}>
                        {analysisType === 'doing-well'
                          ? 'Consider sharing successful patterns with other teams or documenting best practices for future reference.'
                          : 'Focus on one key area at a time. Small, consistent changes often lead to the biggest improvements in team wellbeing.'}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Footer Actions */}
                <div className="flex justify-between items-center pt-8 border-t border-gray-200">
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowAnalysisModal(false)}
                      className="px-6 py-3"
                    >
                      Close
                    </Button>
                    <Button 
                      variant="ghost"
                      className="px-6 py-3 text-gray-600"
                      onClick={exportReportViaEmail}
                      disabled={isExportingReport || !teamAnalysis}
                    >
                      {isExportingReport ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>📄 Export Report</>
                      )}
                    </Button>
                  </div>
                  <Button 
                    onClick={() => generateTeamWellbeingAnalysis(analysisType)}
                    disabled={isGeneratingAnalysis}
                    className={`px-8 py-3 font-semibold text-white shadow-lg hover:shadow-xl transition-all duration-200 ${
                      analysisType === 'doing-well' 
                        ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700' 
                        : 'bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700'
                    }`}
                  >
                    <span className="mr-2">🔄</span>
                    Refresh Analysis
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule 1:1s Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">📅 Schedule 1:1 Meetings</DialogTitle>
            <DialogDescription>
              Select a team member to find optimal meeting times
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Team Member Selection */}
            <div>
              <h3 className="text-sm font-medium mb-3">Select Team Member</h3>
              <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto">
                {teamMembers.map((member) => (
                  <Button
                    key={member.id}
                    variant={selectedTeamMember?.id === member.id ? "default" : "outline"}
                    className="justify-start"
                    onClick={() => handleTeamMemberSelect(member)}
                  >
                    <Avatar className="h-6 w-6 mr-2">
                      <AvatarImage src={member.avatarUrl || undefined} />
                      <AvatarFallback>{(member.name || 'U')?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    {member.name || 'Unknown User'}
                  </Button>
                ))}
              </div>
            </div>

            {/* Meeting Type and Duration Selection */}
            {selectedTeamMember && (
              <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">Meeting Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-blue-700 dark:text-blue-300 block mb-1">Meeting Type</label>
                    <select 
                      value={meetingType} 
                      onChange={(e) => setMeetingType(e.target.value as any)}
                      className="w-full p-2 text-sm border-2 border-blue-200 dark:border-blue-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    >
                      <option value="one-on-one">One-on-One</option>
                      <option value="team-standup">Team Standup</option>
                      <option value="project-review">Project Review</option>
                      <option value="feedback-session">Feedback Session</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-blue-700 dark:text-blue-300 block mb-1">Duration</label>
                    <select 
                      value={meetingDuration} 
                      onChange={(e) => {
                        const newDuration = Number(e.target.value);
                        setMeetingDuration(newDuration);
                        // Clear existing data and reload slots when duration changes
                        setShowFlexibleScheduler(false);
                        setDetailedAvailability(null);
                        setSelectedTimeSlot(null);
                        if (selectedTeamMember) {
                          setIsLoadingSlots(true);
                          loadAvailableSlots(selectedTeamMember.id, newDuration);
                        }
                      }}
                      className="w-full p-2 text-sm border-2 border-blue-200 dark:border-blue-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                    >
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={45}>45 minutes</option>
                      <option value={60}>1 hour</option>
                      <option value={90}>1.5 hours</option>
                      <option value={120}>2 hours</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Meeting Preferences & Calendar Status */}
            {selectedTeamMember && (
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h3 className="text-sm font-medium">Meeting Details</h3>
                <div className="space-y-2 text-sm">
                  <p>📍 Meeting Type: {meetingType.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                  <p>⏱️ Duration: {meetingDuration} minutes</p>
                  <p>📅 Looking for times in the next 7 days</p>
                  <div className="flex items-center gap-2 mt-3 p-2 rounded border">
                    {hasCalendar ? (
                      <>
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-green-700 dark:text-green-400 font-medium">
                          ✅ Google Calendar Connected - Showing real availability
                        </span>
                      </>
                    ) : (
                      <>
                        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                        <span className="text-amber-700 dark:text-amber-400 font-medium">
                          ⚠️ Calendar not connected - Showing estimated times
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleConnectCalendar}
                          disabled={isConnectingCalendar}
                          className="ml-auto"
                        >
                          {isConnectingCalendar ? (
                            <>
                              <div className="animate-spin w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full mr-1"></div>
                              Connecting...
                            </>
                          ) : (
                            "Connect Calendar"
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Scheduling Options */}
            {selectedTeamMember && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-medium">Choose Scheduling Method</h3>
                  <div className="flex gap-2">
                    <Button
                      variant={!showFlexibleScheduler ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowFlexibleScheduler(false)}
                    >
                      Quick Suggestions
                    </Button>
                    <Button
                      variant={showFlexibleScheduler ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowFlexibleScheduler(true)}
                    >
                      Flexible Picker
                    </Button>
                  </div>
                </div>

                {isLoadingSlots ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full mr-2"></div>
                    <span className="text-sm text-muted-foreground">
                      {hasCalendar ? "Checking both calendars for availability..." : "Finding available times..."}
                    </span>
                  </div>
                ) : showFlexibleScheduler ? (
                  /* Flexible Calendar View */
                  <div className="space-y-4">
                    {detailedAvailability && detailedAvailability.days.length > 0 ? (
                      <div className="space-y-4">
                        <div className="text-xs text-muted-foreground">
                          📊 Found {detailedAvailability.days.reduce((total, day) => total + day.timeSlots.filter(slot => slot.available).length, 0)} available {meetingDuration}-minute slots across {detailedAvailability.days.length} days
                          {detailedAvailability.totalConflicts > 0 && ` (${detailedAvailability.totalConflicts} conflicts detected)`}
                          <br />
                          📅 Calendar Status: 
                          {detailedAvailability.organizerHasCalendar ? " Your calendar connected" : " Your calendar not connected"}
                          {detailedAvailability.attendeeHasCalendar ? " | Team member's calendar connected" : " | Team member's calendar not connected"}
                          {!detailedAvailability.organizerHasCalendar && !detailedAvailability.attendeeHasCalendar && " | Showing estimated availability"}
                        </div>
                        
                        {detailedAvailability.days.map((day, dayIndex) => (
                          <div key={dayIndex} className="border rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-medium">{format(day.date, 'EEEE, MMMM d')}</h4>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-green-600">
                                  {day.timeSlots.filter(slot => slot.available).length} available
                                </span>
                                {day.hasConflicts && (
                                  <span className="text-amber-600">
                                    {day.timeSlots.filter(slot => !slot.available).length} conflicts
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-8 gap-1">
                              {day.timeSlots.map((slot, slotIndex) => (
                                <button
                                  key={slotIndex}
                                  onClick={() => {
                                    if (slot.available) {
                                      setSelectedTimeSlot(slot);
                                    }
                                  }}
                                  disabled={!slot.available}
                                  className={`
                                    p-2 text-xs rounded border text-center transition-all
                                    ${slot.available 
                                      ? selectedTimeSlot?.start.getTime() === slot.start.getTime()
                                        ? 'bg-blue-600 text-white border-blue-600' 
                                        : 'bg-green-50 hover:bg-green-100 border-green-200 text-green-800'
                                      : 'bg-red-50 border-red-200 text-red-500 cursor-not-allowed opacity-50'
                                    }
                                  `}
                                  title={slot.available ? `Available: ${format(slot.start, 'h:mm a')}` : `Conflict: ${slot.conflicts?.join(', ')}`}
                                >
                                  {format(slot.start, 'h:mm')}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        
                        {selectedTimeSlot && (
                          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Selected Time</p>
                                <p className="text-sm text-muted-foreground">
                                  {format(selectedTimeSlot.start, 'EEEE, MMM d, yyyy \'at\' h:mm a')} - {format(selectedTimeSlot.end, 'h:mm a')}
                                </p>
                              </div>
                              <Button
                                onClick={() => handleBookMeeting()}
                                disabled={isScheduling}
                              >
                                {isScheduling ? (
                                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                ) : (
                                  "Book Meeting"
                                )}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No detailed availability loaded yet.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Quick Suggestions View */
                  <div className="space-y-2">
                    {availableSlots.length > 0 ? (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground mb-3">
                          🚀 Quick suggestions based on {hasCalendar ? 'both calendars' : 'estimated availability'}
                        </p>
                        {availableSlots.map((slot, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                          >
                            <div>
                              <p className="font-medium">{format(slot.start, 'EEEE, MMM d')}</p>
                              <p className="text-sm text-muted-foreground">
                                {format(slot.start, 'h:mm a')} - {format(slot.end, 'h:mm a')}
                              </p>
                            </div>
                            <Button
                              size="sm"
                              onClick={() => handleBookMeeting(slot)}
                              disabled={isScheduling}
                            >
                              {isScheduling ? (
                                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                "Book"
                              )}
                            </Button>
                          </div>
                        ))}
                        
                        {/* Button to switch to flexible scheduler */}
                        <div className="text-center pt-4 border-t">
                          <Button
                            variant="outline"
                            onClick={async () => {
                              if (!user?.id || !selectedTeamMember?.id) return;
                              setIsLoadingSlots(true);
                              setShowFlexibleScheduler(true);
                              try {
                                const startDate = new Date().toISOString();
                                const endDate = addDays(new Date(), 7).toISOString();
                                const availability = await meetingsService.getCommonAvailability({
                                  userIds: [user.id, selectedTeamMember.id],
                                  startDate,
                                  endDate,
                                  duration: meetingDuration,
                                });
                                setDetailedAvailability(availability);
                              } catch (error) {
                                console.error("Error loading detailed availability:", error);
                              } finally {
                                setIsLoadingSlots(false);
                              }
                            }}
                            className="text-sm"
                          >
                            🗓️ Show more flexible times
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">
                          No available times found in the next 7 days.
                        </p>
                        <Button
                          variant="outline"
                          onClick={async () => {
                            if (!user?.id || !selectedTeamMember?.id) return;
                            setIsLoadingSlots(true);
                            setShowFlexibleScheduler(true);
                            try {
                              const startDate = new Date().toISOString();
                              const endDate = addDays(new Date(), 7).toISOString();
                              const availability = await meetingsService.getCommonAvailability({
                                userIds: [user.id, selectedTeamMember.id],
                                startDate,
                                endDate,
                                duration: meetingDuration,
                              });
                              setDetailedAvailability(availability);
                            } catch (error) {
                              console.error("Error loading detailed availability:", error);
                            } finally {
                              setIsLoadingSlots(false);
                            }
                          }}
                          className="mt-2"
                        >
                          🗓️ Try flexible picker
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedTeamMember(null);
                  setAvailableSlots([]);
                  setDetailedAvailability(null);
                  setSelectedTimeSlot(null);
                  setShowFlexibleScheduler(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Team Goal Setting Modal */}
      <GoalSettingModal
        isOpen={showGoalModal}
        onOpenChange={setShowGoalModal}
        onSubmit={handleCreateTeamGoal}
      />

      {/* Team Update Modal */}
      <Dialog open={showUpdateModal} onOpenChange={setShowUpdateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              Send Team Update
            </DialogTitle>
            <DialogDescription>
              Share important announcements or updates with your team.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="update-title">Title</Label>
              <Input
                id="update-title"
                placeholder="Update title"
                value={teamUpdate.title}
                onChange={(e) => setTeamUpdate({ ...teamUpdate, title: e.target.value })}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="update-message">Message</Label>
              <Textarea
                id="update-message"
                placeholder="Share your update with the team..."
                value={teamUpdate.message}
                onChange={(e) => setTeamUpdate({ ...teamUpdate, message: e.target.value })}
                rows={4}
              />
            </div>
            
            <div className="grid gap-2">
              <Label>Type</Label>
              <div className="flex gap-2">
                {["announcement", "goal", "celebration"].map((type) => (
                  <Button
                    key={type}
                    variant={teamUpdate.type === type ? "default" : "outline"}
                    size="sm"
                    onClick={() => setTeamUpdate({ ...teamUpdate, type: type as any })}
                  >
                    {type === "announcement" ? "📢" : type === "goal" ? "🎯" : "🎉"} {type}
                  </Button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowUpdateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendTeamUpdate}
              disabled={isSendingUpdate || !teamUpdate.title || !teamUpdate.message}
            >
              {isSendingUpdate ? "Sending..." : "Send Update"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardOverview;
